import moment from "moment";
import DatePicker from "../../components/shared/DatePicker/DatePicker";
import BtButton from "../bt-button/bt-button";
import BtChecklistSelect from '../bt-checklist-select/bt-checklist-select';
import { FC, useMemo } from 'react';
import { regions } from "../../staticData/regions";
import { TrashIcon } from "../../components/svgs";
import { useTranslation } from "react-i18next";

type Region = { label: string; value: string };
type Area = { id: string; label: string; value: string };
// Ad applications carry their categories as {label, value: id}, with no id.
type AdArea = { id?: string; label: string; value: string };
type Ad = {
  startDate?: Date;
  endDate?: Date;
  // An ad under review keeps a region that is no longer offered as an empty entry.
  regions?: (Region | null | undefined)[];
  areas?: AdArea[];
  errors?: Record<string, string>;
};

const areaIdOf = (area: AdArea) => area.id ?? area.value;

type Props = {
  index: number,
  ad: Ad,
  setState: (update: (ads: Ad[]) => Ad[]) => void,
  areas: Area[],
  deletable?: boolean,
}

const RecommendedAd: FC<Props> = ({ index, ad, areas, setState, deletable }) => {
  const [t] = useTranslation('marketing', { keyPrefix: 'recommendedAd' });
  const areaOptions = useMemo(
    () => areas.map(area => ({ value: area.id, label: area.label })),
    [areas],
  );
  return (
    <div key={index} className={`supplierAdPlacement ${deletable ? 'supplierAdPlacementDeletable' : ''}`}>
      <DatePicker
        label={t('label.startDate', 'Startdatum')}
        selected={ad?.startDate}
        maxDate={ad?.endDate && moment(ad?.endDate).toDate()}
        onChange={(value) => setState(ads => {
          ads[index].startDate = value;

          return ads;
        })}
      />
      {deletable && (
        <>
          <DatePicker
            label={t('label.endDate', 'Slutdatum')}
            selected={ad?.endDate}
            minDate={moment(ad?.startDate).toDate()}
            onChange={(value) => setState(ads => {
              ads[index].endDate = value;
              return ads;
            })}
          />

          <BtButton
            color='white'
            size='xxs'
            leftIcon={<TrashIcon size='14px' color='var(--gray-700)' />}
            onClick={() => setState(ads => ads.filter((_, _index) => index !== _index))}
          />
        </>
      )}
      <BtChecklistSelect
        label={t('label.region', 'Län')}
        options={regions}
        value={ad?.regions?.flatMap(region => (region ? [region.value] : [])) ?? []}
        onChange={values =>
          setState(ads => {
            ads[index].regions = values.flatMap(
              value => regions.find(region => region.value === value) ?? [],
            );
            ads[index].errors = { ...ads[index].errors, regions: '' };
            return ads;
          })
        }
        errorMessage={ad?.errors?.regions}
        required
      />
      <BtChecklistSelect
        label={t('label.areas', 'Yrkeskategorier')}
        options={areaOptions}
        value={ad?.areas?.map(areaIdOf) ?? []}
        onChange={ids =>
          setState(ads => {
            const current = ads[index].areas ?? [];
            // The ad's own entries go back as they came, since an ad application saves their value.
            const areaOf = (id: string) =>
              current.find(area => areaIdOf(area) === id) ?? areas.find(area => area.id === id);
            ads[index].areas = ids.flatMap(id => areaOf(id) ?? []);
            ads[index].errors = { ...ads[index].errors, areas: '' };
            return ads;
          })
        }
        errorMessage={ad?.errors?.areas}
        required
      />
    </div>
  )
}

export default RecommendedAd;
